import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  useGetCreditAgreementArtifactQuery,
  useGetCurrentCreditAgreementFullArtifactQuery,
} from 'packages/creditAgreements/requests/requests.gql.generated';
import { BorrowingBaseMechanics } from 'packages/creditAgreements/ui/CreditAgreementArtifact/sections/BorrowingBaseMechanics';
import { ReportingDeliverables } from 'packages/creditAgreements/ui/CreditAgreementArtifact/sections/ReportingDeliverables';
import { CreditAgreementDownload } from 'packages/creditAgreements/ui/CreditAgreementDownload';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementContextProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { FacilityMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FacilityMechanics';
import { FinancialCovenants } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FinancialCovenants';
import { FundingRequestMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FundingRequestMechanics';
import { PortfolioCovenants } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/PortfolioCovenants';
import { SettlementMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/SettlementMechanics';

const PortfolioCreditAgreement: React.FC<{
  creditAgreementId: string;
  creditFacilityId: string;
  orgSlug: string;
}> = ({ creditAgreementId, creditFacilityId, orgSlug }) => {
  const toast = useToast();
  const history = useHistory();

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCreditAgreementArtifactQuery({
    variables: { creditAgreementId },
  });

  if (
    isCreditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.creditAgreementArtifact
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-credit-agreement-loading"
      />
    );
  }

  const {
    creditAgreementArtifact: { creditAgreement, id },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
    finleyAssert(
      parsedCreditAgreement,
      `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
    );

    const {
      borrowingBaseMechanics,
      fundingRequestMechanics,
      facilityMechanics,
      settlementMechanics,
      financialCovenants,
      portfolioCovenants,
      reportingDeliverables,
    } = parsedCreditAgreement;

    return (
      <Box>
        <CreditAgreementContextProvider
          initOrgSlugs={[orgSlug]}
          parties={facilityMechanics.parties}
        >
          <CreditAgreementDownload creditFacilityId={creditFacilityId} />
          <Box h="36px" />
          <FacilityMechanics facilityMechanics={facilityMechanics} />
          <FundingRequestMechanics
            fundingRequestMechanics={fundingRequestMechanics}
          />
          <SettlementMechanics settlementMechanics={settlementMechanics} />
          <BorrowingBaseMechanics
            borrowingBaseMechanics={borrowingBaseMechanics}
          />
          <FinancialCovenants financialCovenants={financialCovenants} />
          <PortfolioCovenants portfolioCovenants={portfolioCovenants} />
          <ReportingDeliverables
            reportingDeliverables={reportingDeliverables}
          />
        </CreditAgreementContextProvider>
      </Box>
    );
  } catch (err) {
    Sentry.captureException(err);
    errorToastConfig.title = `Error parsing credit agreement artifact for credit agreement ${id}`;
    toast(errorToastConfig);
    history.push('/');
  }
  throw new Error('unreachable code');
};

export const PortfolioCreditAgreementContainer: React.FC<{
  creditFacilityId: string;
  orgSlug: string;
}> = ({ creditFacilityId, orgSlug }) => {
  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });

  if (
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact ||
    isCreditAgreementLoading
  ) {
    return <Skeleton data-testid="loading-credit-agreement" height="600px" />;
  }

  const {
    currentCreditAgreementFullArtifact: { id: creditAgreementId },
  } = creditAgreementData;

  return (
    <PortfolioCreditAgreement
      creditAgreementId={creditAgreementId}
      creditFacilityId={creditFacilityId}
      orgSlug={orgSlug}
    />
  );
};
